import { type RefObject, useEffect, useState } from 'react';

export const MOBILE_SECTION_TOP_OFFSET_RATIO = 0.25;
export const MOBILE_STICKY_NAVIGATION_MAX_WIDTH = 1023;
export const MOBILE_STICKY_NAVIGATION_HEIGHT = 156;

interface MobileStickyNavigationState {
  isActive: boolean;
  stickyTop: number;
}

export const getMobileViewportHeight = (): number =>
  window.visualViewport?.height ?? window.innerHeight;

const getStickyNavigationTop = (): number =>
  Math.max(
    getMobileViewportHeight() - MOBILE_STICKY_NAVIGATION_HEIGHT,
    0,
  );

const hasReachedSectionEntryPoint = (section: HTMLElement): boolean => {
  const { top } = section.getBoundingClientRect();

  return top <= getMobileViewportHeight() * MOBILE_SECTION_TOP_OFFSET_RATIO;
};

export const useMobileStickyNavigation = <T extends HTMLElement>(
  sectionRef: RefObject<T | null>,
): MobileStickyNavigationState => {
  const [navigationState, setNavigationState] =
    useState<MobileStickyNavigationState>({
      isActive: false,
      stickyTop: 0,
    });

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return;
    }

    const updateNavigation = (): void => {
      const isMobileViewport =
        window.innerWidth <= MOBILE_STICKY_NAVIGATION_MAX_WIDTH;
      const nextState = {
        isActive:
          isMobileViewport && hasReachedSectionEntryPoint(section),
        stickyTop: isMobileViewport ? getStickyNavigationTop() : 0,
      };

      setNavigationState((currentState) =>
        currentState.isActive === nextState.isActive &&
        currentState.stickyTop === nextState.stickyTop
          ? currentState
          : nextState,
      );
    };

    const visualViewport = window.visualViewport;

    window.addEventListener('resize', updateNavigation);
    window.addEventListener('scroll', updateNavigation, { passive: true });
    visualViewport?.addEventListener('resize', updateNavigation);
    visualViewport?.addEventListener('scroll', updateNavigation);
    updateNavigation();

    return () => {
      window.removeEventListener('resize', updateNavigation);
      window.removeEventListener('scroll', updateNavigation);
      visualViewport?.removeEventListener('resize', updateNavigation);
      visualViewport?.removeEventListener('scroll', updateNavigation);
    };
  }, [sectionRef]);

  return navigationState;
};
